/**
 * ====================================================================
 * FIKO CAD DIMENSION SYSTEM - PUBLIC API
 * ====================================================================
 *
 * CATIA/IronCAD/SolidWorks seviyesinde profesyonel ölçülendirme sistemi.
 * v2.0.0 - Gelişmiş görsel efektler, animasyonlar ve ayarlar sistemi.
 *
 * Kullanım:
 * ```typescript
 * import { DimensionManager, DimensionType, ReferenceType, DimensionSettings } from './dimension';
 *
 * // Ayarları yapılandır
 * const settings = DimensionSettings.getInstance();
 * settings.applyPreset('catia'); // veya 'solidworks', 'autocad', 'fikoyt', 'minimal'
 *
 * const manager = new DimensionManager(fikoSahne);
 *
 * // Linear dimension
 * manager.createLinearDimension(
 *     { type: ReferenceType.POINT, point: new Vector3(0, 0, 0) },
 *     { type: ReferenceType.POINT, point: new Vector3(10, 0, 0) }
 * );
 *
 * // Diameter dimension
 * manager.createDiameterDimension({
 *     type: ReferenceType.CIRCLE,
 *     center: new Vector3(0, 0, 0),
 *     radius: 5
 * });
 * ```
 *
 * @author FikoYT CAD Team
 * @version 2.0.0
 */

// ====================================================================
// TYPES & INTERFACES
// ====================================================================
// Enums (re-export as values)
export {
    DimensionType,
    ReferenceType,
    AxisLock,
    TextPlacement,
    ArrowStyle,
    DimensionState,
    DimensionErrorType,
    Dimension,
    DIMENSION_CONSTANTS
} from './DimensionTypes';

// Types/Interfaces (re-export as types for isolatedModules compatibility)
export type {
    GeometryReference,
    TwoPointReference,
    SingleReference,
    ThreePointReference,
    BaseDimensionOptions,
    LinearDimensionOptions,
    AngularDimensionOptions,
    DiameterDimensionOptions,
    DimensionResult,
    DimensionError,
    FallbackInfo,
    DimensionBounds,
    OverlapResult,
    PlacementSuggestion
} from './DimensionTypes';

// ====================================================================
// DIMENSION CLASSES
// ====================================================================
export { LinearDimension } from './LinearDimension';
export { AngularDimension, AngularDimension as AngleDimension } from './AngleDimension';
export { DiameterDimension } from './DiameterDimension';
export { RadiusDimension } from './RadiusDimension';
export { CoordinateDimension, type CoordinateFrame, type CoordinateDimensionOptions } from './CoordinateDimension';
export { AreaDimension, type AreaDimensionOptions, type AreaCalculationResult } from './AreaDimension';
export { ArcDimension, type ArcDimensionOptions, type ArcProperties } from './ArcDimension';
export { MinimumDistanceDimension, type MinimumDistanceDimensionOptions, type MinimumDistanceResult } from './MinimumDistanceDimension';
export { ChainDimension, type ChainDimensionOptions, type ChainSegment } from './ChainDimension';
export { BaselineDimension, type BaselineDimensionOptions, type BaselineSegment } from './BaselineDimension';
export { OrdinateDimension, OrdinateAxis, type OrdinateDimensionOptions, type OrdinateInfo } from './OrdinateDimension';

// ====================================================================
// SMART MEASURE SYSTEM
// ====================================================================
export {
    SmartMeasureMode,
    SingleMeasurementType,
    DoubleMeasurementType,
    type SmartMeasureResult
} from './SmartMeasureMode';

export {
    GeometryRecognizer,
    RecognizedGeometryType,
    type GeometryFeature,
    type CircleFeature,
    type LineFeature,
    type PlaneFeature,
    type CylinderFeature,
    type ConeFeature,
    type SphereFeature,
    type TorusFeature,
    type RecognitionResult
} from './GeometryRecognizer';

// ====================================================================
// MANAGERS
// ====================================================================
export { DimensionManager } from './DimensionManager';
export { LayoutManager, type LayoutCheckResult } from './LayoutManager';
export { FallbackResolver, FallbackStrategy, type FallbackResult } from './FallbackResolver';
export { DraggableSupport } from './DraggableSupport';

// ====================================================================
// NEW VISUAL SYSTEM (v2.0.0)
// ====================================================================
// Settings
export { DimensionSettings, type SettingsChangeEvent, type SettingsChangeListener } from './DimensionSettings';
export {
    STYLE_PRESETS,
    DEFAULT_VISUAL_SETTINGS,
    ExtendedArrowStyle,
    LineStyleType
} from './DimensionSettings';
export type {
    DimensionVisualSettings,
    PresetName,
    DimensionColorPalette,
    AnimationConfig,
    GlowEffectConfig,
    TooltipConfig,
    SmartPlacementConfig,
    ArrowStyleConfig,
    LineStyleConfig,
    LabelStyleConfig
} from './DimensionSettings';

// Visual effects
export { DimensionGlowEffect } from './visual/DimensionGlowEffect';
export { DimensionAnimator, Easing } from './visual/DimensionAnimator';
export type { EasingType } from './visual/DimensionAnimator';
export { DimensionTooltip } from './visual/DimensionTooltip';

// Geometry factories
export { ArrowFactory } from './geometry/ArrowFactory';
export { LineStyleFactory, DASH_PATTERNS } from './geometry/LineStyleFactory';
export { LeaderLineRenderer } from './geometry/LeaderLineRenderer';
export type { LeaderGeometry, LeaderLineGroup } from './geometry/LeaderLineRenderer';

// Layout
export { SmartLabelPlacer } from './layout/SmartLabelPlacer';
export type { LabelBounds, LabelPlacement } from './layout/SmartLabelPlacer';

// ====================================================================
// CONVENIENCE FACTORY
// ====================================================================
import { DimensionManager } from './DimensionManager';
import { FikoSahne } from '../../FikoSahne';
import { ArrowStyle } from './DimensionTypes';

/**
 * DimensionManager factory fonksiyonu
 */
export function createDimensionManager(scene: FikoSahne): DimensionManager {
    return new DimensionManager(scene);
}

// ====================================================================
// DEFAULT STYLES (CATIA/IRONCAD PROFESSIONAL)
// ====================================================================
export const PROFESSIONAL_DIMENSION_STYLE = {
    // Çizgi stilleri - Siyah, profesyonel
    color: 0x000000,              // 🎯 TAM SİYAH
    lineWidth: 1.5,               // Kalın çizgi (CATIA tarzı)

    // Yazı stilleri
    textColor: '#000000',         // Siyah yazı
    fontSize: 12,                 // 🎯 Kompakt okunabilir boyut
    fontFamily: 'Arial, sans-serif',

    // Ok stilleri - CATIA tarzı büyük oklar
    arrowStyle: ArrowStyle.FILLED,
    arrowSize: 0.6,               // Büyük profesyonel oklar

    // Offset
    offset: 10,
    textOffset: 3,

    // Extension lines
    showExtensionLines: true,
    extensionLineGap: 1.5,
    extensionLineOvershoot: 2,

    // Davranış - Sürükleme kullanıcı kontrolünde
    draggable: false,
    selectable: true,

    // Birim
    precision: 2,
    unit: 'mm',
    showUnit: true
};

/**
 * Siyah profesyonel stil uygula
 */
export function applyProfessionalStyle(options: any = {}): any {
    return {
        ...PROFESSIONAL_DIMENSION_STYLE,
        ...options
    };
}

// ====================================================================
// ADVANCED DIMENSION SYSTEM (v2.0.0)
// ====================================================================
// Next-gen 3D measurement and visualization system
// Includes: Advanced dimensions, interactive arrows, measurement arcs,
// smart lines, 3D annotations, and visual effects

export * from './advanced';
